/**
 * CorrectionDrawer — RSC correction surface (post-dispatch signer fixups).
 *
 * Two flows, both routed at `/signing/correction`:
 *   1. Replace signer     — swap the SignNow invite to a different person
 *                           (typo in email, customer no longer reachable).
 *   2. Correct fields     — void specific documents inside the envelope and
 *                           re-issue (SignNow 2-step correction).
 *
 * Pre-submit (no signer has signed yet) → officer authority OK.
 * Post-submit (at least one signer has signed) → Supervisor authority only.
 *
 * The per-envelope correction cap comes from
 * `/v3/config/signing-rules.maxCorrectionsPerEnvelope`; when unseeded the
 * cap displays as "—" and submission is allowed (degrade-gracefully).
 *
 * Ported from:
 *   C:/Projects/retail-account-opening/spfx-account-opening-v2/.../
 *     components/signing/CorrectionRequestModal.tsx  (form UX, cap counting)
 *     services/CorrectionService.ts                   (transport)
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { ArrowLeft, CheckCircle2, AlertTriangle, UserPlus, Edit3 } from 'lucide-react';
import { Button, Pill, WidgetCard } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from '@retail-ops/shared-types/enums';
import type { SigningCorrectionType } from '@retail-ops/shared-types';
import {
  CorrectionService,
  deriveCorrectionMode,
  type CorrectionMode,
} from './services/CorrectionService';
import { CorrectionCapBanner } from './components/CorrectionCapBanner';
import { useCorrectionCap } from './hooks/useCorrectionCap';
import './rsc.css';

const SUPERVISORY_ROLES: ReadonlyArray<UserRole> = [
  'Supervisor',
  'Manager',
  'Executive',
  'SuperAdmin',
];

type Tab = 'replace-signer' | 'correct-fields';

const CORRECTION_TYPE_OPTIONS: ReadonlyArray<{
  value: SigningCorrectionType;
  label: string;
}> = [
  { value: 'FieldTypo', label: 'Field typo' },
  { value: 'WrongData', label: 'Wrong data' },
  { value: 'MissingInitials', label: 'Missing initials' },
  { value: 'Other', label: 'Other' },
];

export function CorrectionDrawer(): JSX.Element {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { user } = useAuth();

  const role = params.get('role') ?? 'Primary';
  const statusParam = params.get('status') ?? undefined;
  const groupIdParam = params.get('groupId') ?? undefined;

  const [sessionStatus, setSessionStatus] = useState<string | undefined>(statusParam);
  const [tab, setTab] = useState<Tab>('replace-signer');

  // Tab 1 — replace signer (SPFx CorrectionRequestModal replacement path).
  const [oldEmail, setOldEmail] = useState('');
  const [name, setName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');

  // Tab 2 — field corrections.
  const [correctionType, setCorrectionType] = useState<SigningCorrectionType>('FieldTypo');
  const [documentIdsRaw, setDocumentIdsRaw] = useState<string>('');

  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ kind: Tab } | null>(null);

  // CorrectionService is stable across renders because apiClient is memoized
  // in ApiClientContext.
  const correctionService = useMemo(() => new CorrectionService(apiClient), [apiClient]);

  // Pull the envelope-scoped cap + per-role count.
  const {
    cap,
    count,
    loading: capLoading,
    refresh: refreshCap,
  } = useCorrectionCap({
    sessionId,
    role,
    correctionService,
    apiClient,
  });

  // Session status probe — only if the caller didn't pass it on the query.
  useEffect(() => {
    if (!sessionId || statusParam) return;
    let cancelled = false;
    void (async () => {
      try {
        const resp = await apiClient.signing.getSessionStatus(sessionId);
        if (!cancelled) setSessionStatus(resp.Status);
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClient, sessionId, statusParam]);

  const mode = useMemo<CorrectionMode>(() => deriveCorrectionMode(sessionStatus), [sessionStatus]);
  const currentRole = user?.primaryRole;
  const isSupervisor = currentRole !== undefined && SUPERVISORY_ROLES.includes(currentRole);
  const authorityOk = mode === 'pre-submit' ? true : isSupervisor;
  const atCap = cap !== undefined && count >= cap;

  // --- Validation -----------------------------------------------------------
  const canSubmitReplace =
    oldEmail.trim().length > 0 &&
    name.trim().length > 0 &&
    newEmail.trim().length > 0 &&
    reason.trim().length >= 3 &&
    authorityOk &&
    !atCap &&
    !capLoading;

  const parsedDocIds = documentIdsRaw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const canSubmitFields =
    parsedDocIds.length > 0 &&
    reason.trim().length >= 3 &&
    authorityOk &&
    !atCap &&
    !capLoading &&
    !!groupIdParam;

  // --- Submit handlers ------------------------------------------------------

  const submitReplacement = async (): Promise<void> => {
    if (!sessionId || !user) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await correctionService.replaceSigner(
        {
          signingSessionId: sessionId,
          oldSignerEmail: oldEmail.trim(),
          newSigner: {
            Name: name.trim(),
            Email: newEmail.trim(),
            ...(newPhone.trim() ? { Phone: newPhone.trim() } : {}),
            Role: role,
            Method: newPhone.trim() ? 'sms' : 'email',
            InPerson: false,
            SigningOrder: 1,
          },
          requestedBy: user.upn,
          reason: reason.trim(),
        },
        mode,
        role,
      );
      if (!resp.success) {
        setError(resp.error ?? 'Correction failed.');
        return;
      }
      await writeCorrectedEvent('signing-corrected', {
        sessionId,
        role,
        mode,
        kind: 'replace-signer',
        oldEmail,
        newEmail,
        newPhone,
        reason,
      });
      refreshCap();
      setDone({ kind: 'replace-signer' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Correction failed.');
    } finally {
      setBusy(false);
    }
  };

  const submitFieldCorrection = async (): Promise<void> => {
    if (!sessionId || !user || !groupIdParam) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await correctionService.requestFieldCorrection({
        sessionId,
        groupId: groupIdParam,
        documentIds: parsedDocIds,
        reason: reason.trim(),
        correctionType,
        requestedBy: user.upn,
        signerRole: role,
        mode,
      });
      if (!resp.success) {
        setError(resp.error ?? 'Correction failed.');
        return;
      }
      await writeCorrectedEvent('signing-corrected', {
        sessionId,
        role,
        mode,
        kind: 'correct-fields',
        groupId: groupIdParam,
        documentIds: parsedDocIds,
        correctionType,
        reason,
        correctionId: resp.correctionId,
      });
      refreshCap();
      setDone({ kind: 'correct-fields' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Correction failed.');
    } finally {
      setBusy(false);
    }
  };

  const writeCorrectedEvent = async (
    eventType: string,
    payload: Record<string, unknown>,
  ): Promise<void> => {
    try {
      await apiClient.retail.events.write({
        eventType,
        eventCategory: 'signing',
        severity: 'info',
        actor: user?.upn,
        sessionId,
        payloadJson: JSON.stringify(payload),
      });
    } catch {
      /* fire-and-forget */
    }
  };

  const contactSupervisor = async (): Promise<void> => {
    // Out-of-band recovery task — v3 equivalent of the SPFx
    // `createTask` call in CorrectionRequestModal:232.
    if (!sessionId || !user) return;
    try {
      await apiClient.retail.tasks.create({
        title: `Signing correction cap reached — session ${sessionId}`,
        type: 'signing-recovery',
        category: 'signing-recovery',
        priority: 'High',
        assignedTo: 'Supervisor',
        description:
          `Envelope has hit its correction cap (${count}). FSR ${user.upn} ` +
          `requested Supervisor assistance. Signer role: ${role}.`,
        sessionId,
        severity: 'warn',
        autoCreated: false,
      });
      await writeCorrectedEvent('signing-correction-cap-escalated', {
        sessionId,
        role,
        count,
        cap,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create Supervisor task.');
    }
  };

  // --- Render ---------------------------------------------------------------

  if (done) {
    return (
      <div className="rsc-drawer">
        <div className="rsc-banner rsc-banner--ok" role="status">
          <CheckCircle2 size={16} className="rsc-inline-icon" />
          <div>
            {done.kind === 'replace-signer'
              ? 'Signer corrected. New invite dispatched.'
              : 'Field correction submitted. The signing session is re-issuing the document.'}
          </div>
        </div>
        <div className="rsc-footer">
          <Button variant="ghost" onClick={() => navigate('..')}>
            Back to session
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rsc-drawer">
      <div className="rsc-detail__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Back to session
        </Button>
        <h1>Correct · {role}</h1>
        <Pill variant={mode === 'pre-submit' ? 'info' : 'warn'}>
          {mode === 'pre-submit' ? 'Pre-submit (officer)' : 'Post-submit (Supervisor)'}
        </Pill>
      </div>

      <CorrectionCapBanner
        count={count}
        cap={cap}
        scopeLabel={`signer ${role}`}
        onContactSupervisor={atCap ? () => void contactSupervisor() : undefined}
      />

      {mode === 'post-submit' && !authorityOk && (
        <div className="rsc-banner rsc-banner--err" role="alert">
          <AlertTriangle size={14} className="rsc-inline-icon" />
          <div>
            Post-submit corrections require Supervisor authority. Your role ({currentRole ?? '—'})
            cannot perform this action.
          </div>
        </div>
      )}

      <div className="rsc-detail__crumb" aria-label="Correction mode">
        <Button
          variant={tab === 'replace-signer' ? 'primary' : 'ghost'}
          startIcon={<UserPlus size={14} />}
          onClick={() => {
            setTab('replace-signer');
            setError(null);
          }}
        >
          Replace signer
        </Button>
        <Button
          variant={tab === 'correct-fields' ? 'primary' : 'ghost'}
          startIcon={<Edit3 size={14} />}
          onClick={() => {
            setTab('correct-fields');
            setError(null);
          }}
        >
          Correct fields
        </Button>
      </div>

      <WidgetCard title="Scope" stripe="pipeline">
        <div className="rsc-field">
          <div className="rsc-drawer__scope-help">
            Mode derived from session status <code>{sessionStatus ?? 'unknown'}</code>.
            Pre-submit = officer authority. Post-submit = Supervisor only.
          </div>
        </div>
      </WidgetCard>

      {tab === 'replace-signer' && (
        <>
          <WidgetCard title="Old signer">
            <div className="rsc-field">
              <label htmlFor="corr-old-email">Old email (identity match)</label>
              <input
                id="corr-old-email"
                type="email"
                value={oldEmail}
                onChange={(e) => setOldEmail(e.target.value)}
                required
              />
            </div>
          </WidgetCard>

          <WidgetCard title="New signer">
            <div className="rsc-field">
              <label htmlFor="corr-name">Name</label>
              <input
                id="corr-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="rsc-field">
              <label htmlFor="corr-new-email">Email</label>
              <input
                id="corr-new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div className="rsc-field">
              <label htmlFor="corr-new-phone">Phone (optional — switches method to SMS)</label>
              <input
                id="corr-new-phone"
                type="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
              />
            </div>
          </WidgetCard>
        </>
      )}

      {tab === 'correct-fields' && (
        <>
          <WidgetCard title="Correction type">
            <div className="rsc-field">
              <label htmlFor="corr-type">Type</label>
              <select
                id="corr-type"
                value={correctionType}
                onChange={(e) => setCorrectionType(e.target.value as SigningCorrectionType)}
              >
                {CORRECTION_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </WidgetCard>

          <WidgetCard title="Documents">
            <div className="rsc-field">
              <label htmlFor="corr-doc-ids">Document IDs (comma or newline separated)</label>
              <textarea
                id="corr-doc-ids"
                value={documentIdsRaw}
                onChange={(e) => setDocumentIdsRaw(e.target.value)}
                placeholder="doc-abc123, doc-xyz789"
              />
              {!groupIdParam && (
                <div className="rsc-drawer__missing-group">
                  Missing <code>groupId</code> — open this drawer from SessionDetail so the
                  SignNow document-group is carried through.
                </div>
              )}
            </div>
          </WidgetCard>
        </>
      )}

      <WidgetCard title="Reason">
        <div className="rsc-field">
          <label htmlFor="corr-reason">Reason (≥3 chars)</label>
          <textarea
            id="corr-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="E.g. 'Email typo — customer confirmed correct address by phone.'"
          />
        </div>
      </WidgetCard>

      {error && (
        <div className="rsc-banner rsc-banner--err" role="alert">
          {error}
        </div>
      )}

      <div className="rsc-footer">
        <Button variant="ghost" onClick={() => navigate('..')}>
          Cancel
        </Button>
        {tab === 'replace-signer' ? (
          <Button
            variant="primary"
            onClick={() => void submitReplacement()}
            disabled={!canSubmitReplace || busy}
          >
            {busy ? 'Submitting…' : atCap ? 'Cap reached' : 'Replace signer'}
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={() => void submitFieldCorrection()}
            disabled={!canSubmitFields || busy}
          >
            {busy ? 'Submitting…' : atCap ? 'Cap reached' : 'Request field correction'}
          </Button>
        )}
      </div>
    </div>
  );
}
